import type { LocaleCode, Difficulty } from "./api/client";
import type { CategoryId } from "@/data/dialogue-categories";
import type { DialogTopic } from "./dialog-data";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CurriculumTopicMeta {
  id: string;
  title: string;
  titleNative: string;
  description: string;
  descriptionNative: string;
  category: CategoryId;
  difficulty: Difficulty;
  speaker: string;
  wordCount: number;
}

export interface CurriculumPhase {
  id: string;
  title: string;
  topics: CurriculumTopicMeta[];
}

export interface CurriculumCatalog {
  locale: LocaleCode;
  nativeLanguage?: string;
  generatedAt: string;
  phases: CurriculumPhase[];
}

// Map native language codes to directory suffixes
const NATIVE_LANG_PATHS: Record<string, string> = {
  en: "",
  ru: "/ru",
  uk: "/uk",
};

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

const catalogCache = new Map<string, CurriculumCatalog>();
const dialogueCache = new Map<string, DialogTopic>();

// ---------------------------------------------------------------------------
// Loaders
// ---------------------------------------------------------------------------

function getNativeLangPath(nativeCode: string): string {
  const short = (nativeCode.split("-")[0] ?? nativeCode).toLowerCase();
  return NATIVE_LANG_PATHS[short] ?? "";
}

export async function loadCatalog(
  locale: LocaleCode,
  nativeCode: string = "en",
): Promise<CurriculumCatalog | null> {
  const langPath = getNativeLangPath(nativeCode);
  const cacheKey = `${locale}${langPath}`;
  const cached = catalogCache.get(cacheKey);
  if (cached) return cached;

  try {
    const res = await fetch(`/data/curriculum/${locale}${langPath}/catalog.json`);
    if (!res.ok) {
      // Fallback to English if native language version not available
      if (langPath) {
        return loadCatalog(locale, "en");
      }
      return null;
    }
    const data: CurriculumCatalog = await res.json();
    catalogCache.set(cacheKey, data);
    return data;
  } catch {
    return null;
  }
}

export async function loadDialogue(
  locale: LocaleCode,
  topicId: string,
  nativeCode: string = "en",
): Promise<DialogTopic | null> {
  const langPath = getNativeLangPath(nativeCode);
  const key = `${locale}${langPath}/${topicId}`;
  const cached = dialogueCache.get(key);
  if (cached) return cached;

  try {
    const res = await fetch(
      `/data/curriculum/${locale}${langPath}/dialogues/${topicId}.json`,
    );
    if (!res.ok) {
      // No pre-generated file for this native language — return null
      // so the caller can trigger in-flight adaptation via the API.
      if (langPath) {
        return null;
      }
      return null;
    }
    const data: DialogTopic = await res.json();
    dialogueCache.set(key, data);
    return data;
  } catch {
    return null;
  }
}
